import { ArrowLeft, ArrowRight, LoaderCircle, ShieldCheck } from "lucide-react";
import { PhoneField } from "./PhoneField.js";
import type { FormEvent } from "react";
import type { FieldErrors } from "../hooks/useQuizLogic.js";
import { TurnstileWidget } from "./TurnstileWidget.js";

interface ContactStepProps {
  submitting: boolean;
  formError: string;
  configError: string;
  configPending: boolean;
  fieldErrors: FieldErrors;
  titleRef: React.RefObject<HTMLHeadingElement>;
  errorSummaryRef: React.RefObject<HTMLDivElement>;
  turnstileSiteKey: string | null;
  turnstileToken: string;
  turnstileResetNonce: number;
  onTurnstileTokenChange: (token: string) => void;
  onBlur: (e: FormEvent<HTMLFormElement>) => void;
  onInput: (e: FormEvent<HTMLFormElement>) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onBack: () => void;
  emailSuggestion: string | null;
  onUseEmailSuggestion: () => void;
  onKeepEmail: () => void;
}

interface ContactFieldProps {
  name: "fullName" | "phone" | "email";
  label: string;
  hint: string;
  placeholder?: string;
  type?: string;
  autoComplete: string;
  error?: string | undefined;
  extraDescribedBy?: string | undefined;
  children?: React.ReactNode;
}

function ContactField({ name, label, hint, placeholder, type = "text", autoComplete, error, extraDescribedBy, children }: ContactFieldProps) {
  const errorId = `${name}-error`;
  const hintId = `${name}-hint`;
  const describedBy = [hintId, error ? errorId : undefined, extraDescribedBy].filter(Boolean).join(" ");
  return (
    <div className="quiz-field">
      <label htmlFor={name}>{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        autoComplete={autoComplete}
        autoCapitalize={name === "fullName" ? "words" : type === "email" ? "none" : undefined}
        placeholder={placeholder}
        spellCheck={type === "email" ? false : undefined}
        required
        maxLength={name === "email" ? 254 : name === "phone" ? 40 : 160}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
      />
      <p className="field-hint" id={hintId}>{hint}</p>
      {error && <p className="quiz-field-error" id={errorId}>{error}</p>}
      {children}
    </div>
  );
}

export function ContactStep({
  submitting,
  formError,
  configError,
  configPending,
  fieldErrors,
  titleRef,
  errorSummaryRef,
  turnstileSiteKey,
  turnstileToken,
  turnstileResetNonce,
  onTurnstileTokenChange,
  onSubmit,
  onInput,
  onBlur,
  onBack,
  emailSuggestion,
  onUseEmailSuggestion,
  onKeepEmail
}: ContactStepProps) {
  return (
    <section className="quiz-content contact-content" aria-labelledby="contact-title">
      <h1 id="contact-title" ref={titleRef} tabIndex={-1}>
        Descubre en qué etapa está tu negocio y cómo avanzar
      </h1>
      <p className="contact-intro">¡Completaste el quiz! Déjanos tus datos para conocer tu resultado, ver el mensaje de Margarita y recibir recomendaciones según tus respuestas.</p>

      {(formError || configError) && (
        <div className="quiz-error-summary" role={Object.keys(fieldErrors).length ? undefined : "alert"} tabIndex={-1} ref={errorSummaryRef}>
          {formError || configError}
        </div>
      )}

      <form onSubmit={onSubmit} onInput={onInput} onBlur={onBlur} noValidate>
        <fieldset className="contact-fields" disabled={submitting}>
        <p className="quiz-instructions">Todos los campos son obligatorios.</p>
        <ContactField
          name="fullName"
          label="Nombre completo"
          placeholder="Ej. Ana López"
          hint="Tu nombre y apellido para personalizar tu experiencia."
          autoComplete="name"
          error={fieldErrors["fullName"]}
        />
        <PhoneField error={fieldErrors["phone"]} />
        <ContactField
          name="email"
          type="email"
          label="Correo electrónico"
          placeholder="Ej. ana@empresa.com"
          hint="Usa un correo al que tengas acceso."
          autoComplete="email"
          error={fieldErrors["email"]}
          extraDescribedBy={emailSuggestion ? "email-suggestion" : undefined}
        >
          {/* Same help as the tour form and the calendar: one click fixes the typo, or the person keeps what they wrote. */}
          <p className="email-suggestion" id="email-suggestion" aria-live="polite">
            {emailSuggestion && <>
              ¿Quisiste decir{" "}
              <button type="button" className="email-suggestion-use" data-email-suggestion-use onClick={onUseEmailSuggestion}>{emailSuggestion}</button>
              ? · <button type="button" className="email-suggestion-keep" onClick={onKeepEmail}>No, está bien</button>
            </>}
          </p>
        </ContactField>
        <div hidden>
          <label htmlFor="website">Sitio web</label>
          <input id="website" name="website" tabIndex={-1} autoComplete="off" />
        </div>
        <label className="quiz-consent" htmlFor="consent">
          <input type="checkbox" id="consent" name="consent" required
            aria-describedby={fieldErrors["consent"] ? "consent-error" : undefined}
            aria-invalid={Boolean(fieldErrors["consent"])} />
          <span>Acepto recibir comunicaciones por correo electrónico y mensajes de texto. Confirmo que he leído y acepto los Términos y Condiciones y la Política de Privacidad.</span>
        </label>
        <p className="quiz-legal-actions">
          <a href="https://www.pasosalexito.com/terminos-y-condiciones/" target="_blank" rel="noreferrer">Términos y Condiciones</a>{" · "}
          <a href="https://www.pasosalexito.com/politica-de-privacidad/" target="_blank" rel="noreferrer">Política de Privacidad</a>
        </p>
        {fieldErrors["consent"] && <p className="quiz-field-error" id="consent-error">{fieldErrors["consent"]}</p>}
        {turnstileSiteKey && (
          <>
            <TurnstileWidget
              siteKey={turnstileSiteKey}
              resetNonce={turnstileResetNonce}
              onTokenChange={onTurnstileTokenChange}
            />
            <input type="hidden" name="turnstileToken" value={turnstileToken} />
            {fieldErrors["turnstileToken"] && (
              <p className="quiz-field-error">{fieldErrors["turnstileToken"]}</p>
            )}
          </>
        )}
        <div className="contact-actions">
          <button className="quiz-icon-button" type="button" onClick={onBack} aria-label="Volver">
            <ArrowLeft size={19} aria-hidden="true" />
          </button>
          <button
            className="quiz-submit"
            type="submit"
            disabled={submitting || configPending || Boolean(configError)}
            aria-busy={submitting || configPending}
          >
            {submitting ? <LoaderCircle className="spin" size={18} aria-hidden="true" /> : <ArrowRight size={18} aria-hidden="true" />}
            {submitting ? "Guardando tus respuestas…" : "Ver mi resultado"}
          </button>
        </div>
        <p className="quiz-assurance">
          <ShieldCheck size={15} aria-hidden="true" />
          Guardamos tu avance en este navegador. Al solicitar tu resultado, enviamos tus datos de contacto y respuestas de forma segura.
        </p>
        </fieldset>
      </form>
    </section>
  );
}
